import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonIcon from '@mui/icons-material/Person';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { fetchWithAuth } from '../lib/api';

const STEPS = [
  'Εισαγωγή',
  'Σύνδεση TaxisNet',
  'Εγγραφή στο REST API',
  'Νέα εγγραφή χρήστη',
  'Αντιγραφή Κλειδιών',
  'Δοκιμή Σύνδεσης',
];

const MYDATA_PORTAL_URL =
  'https://www1.aade.gr/saadeapps2/bookkeeper-web/bookkeeper/#!/apiSubscription?mode=api';

const guideImages = {
  step1: '/images/mydata-guide/mydata-step1-signup.png',
  step2: '/images/mydata-guide/mydata-step2-register.png',
  step3: '/images/mydata-guide/mydata-step3-subscribe.png',
  step3b: '/images/mydata-guide/mydata-step3b-username.png',
  step3c: '/images/mydata-guide/mydata-step3c-confirm.png',
  step3d: '/images/mydata-guide/mydata-step3d-confirm-key.png',
  step4: '/images/mydata-guide/mydata-step4-copy-key.png',
  step4b: '/images/mydata-guide/mydata-step4b-app-fields.png',
  step5: '/images/mydata-guide/mydata-step5-success.png',
};

export default function MyDataSetupWizard({ onComplete, initialVat }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [expandedGuide, setExpandedGuide] = useState({});

  const [userId, setUserId] = useState('');
  const [subscriptionKey, setSubscriptionKey] = useState('');
  const [companyVat, setCompanyVat] = useState(initialVat || '');
  const [showKey, setShowKey] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (initialVat && !companyVat) {
      setCompanyVat(initialVat);
    }
  }, [initialVat]);

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const toggleGuideSection = (key) => {
    setExpandedGuide((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allFieldsFilled = userId.trim() && subscriptionKey.trim() && companyVat.trim();

  const handleTestConnection = async () => {
    if (!allFieldsFilled) {
      setTestResult({
        success: false,
        error: 'Συμπληρώστε και τα 3 πεδία (User ID, Subscription Key, ΑΦΜ) πριν τη δοκιμή.',
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const res = await fetchWithAuth('/api/mydata/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId.trim(),
          subscription_key: subscriptionKey.trim(),
        }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({
        success: false,
        error: 'Σφάλμα δικτύου. Ελέγξτε τη σύνδεσή σας.',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveAndFinish = async () => {
    setSaving(true);
    try {
      await fetchWithAuth('/api/settings/mydata_user_id', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: userId.trim() }),
      });
      await fetchWithAuth('/api/settings/mydata_subscription_key', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: subscriptionKey.trim() }),
      });
      await fetchWithAuth('/api/settings/mydata_company_vat', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: companyVat.trim() }),
      });
      setSaved(true);
      if (onComplete) {
        setTimeout(() => onComplete(), 1500);
      }
    } catch (err) {
      setTestResult({
        success: false,
        error: 'Σφάλμα αποθήκευσης. Δοκιμάστε ξανά.',
      });
    } finally {
      setSaving(false);
    }
  };

  const GuideImage = ({ src, alt }) => (
    <Box
      sx={{
        my: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 1,
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{ width: '100%', display: 'block' }}
        loading="lazy"
      />
    </Box>
  );

  const CollapsibleGuide = ({ title, guideKey, children }) => (
    <Box sx={{ mb: 2 }}>
      <Button
        fullWidth
        variant="outlined"
        size="small"
        onClick={() => toggleGuideSection(guideKey)}
        endIcon={expandedGuide[guideKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          justifyContent: 'space-between',
          textTransform: 'none',
          color: 'text.secondary',
          borderColor: 'divider',
        }}
      >
        {title}
      </Button>
      <Collapse in={expandedGuide[guideKey]}>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );

  const renderStepIntro = () => (
    <Box>
      <Box
        sx={{
          textAlign: 'center',
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.main}05)`,
        }}
      >
        <LinkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Σύνδεση με myDATA
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Συνδέστε την εφαρμογή με το myDATA της ΑΑΔΕ για αυτόματη εισαγωγή τιμολογίων.
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Τι θα χρειαστείτε:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="1" size="small" color="primary" />
              <Typography variant="body2">
                Πρόσβαση στο <strong>TaxisNet</strong> (κωδικοί taxisnet.gr)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="2" size="small" color="primary" />
              <Typography variant="body2">
                Πρόσβαση στο <strong>myDATA Bookkeeper</strong> portal της ΑΑΔΕ
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="3" size="small" color="primary" />
              <Typography variant="body2">
                Περίπου <strong>5 λεπτά</strong> χρόνου
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        Η διαδικασία γίνεται μία φορά. Μετά τη σύνδεση, τα τιμολόγια εισάγονται αυτόματα.
      </Alert>
    </Box>
  );

  const renderStepRegister = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Βήμα 1: Σύνδεση με TaxisNet
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ανοίξτε το myDATA Bookkeeper σε νέα καρτέλα. Θα σας ζητηθεί σύνδεση με τους κωδικούς TaxisNet.
      </Typography>

      <Button
        variant="contained"
        size="large"
        fullWidth
        endIcon={<OpenInNewIcon />}
        onClick={() => window.open(MYDATA_PORTAL_URL, '_blank')}
        sx={{ mb: 3, py: 1.5 }}
      >
        Άνοιγμα myDATA Portal
      </Button>

      <CollapsibleGuide title="Δείτε πώς (με εικόνες)" guideKey="register">
        <Typography variant="body2" sx={{ mb: 1 }}>
          Εισάγετε το <strong>Όνομα χρήστη</strong> και τον <strong>Κωδικό πρόσβασης</strong> TaxisNet και πατήστε <strong>«ΣΥΝΔΕΣΗ»</strong>:
        </Typography>
        <GuideImage src={guideImages.step1} alt="myDATA Portal - TaxisNet login" />
      </CollapsibleGuide>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Αφού συνδεθείτε επιτυχώς, πατήστε «Επόμενο».
      </Typography>
    </Box>
  );

  const renderStepSubscribe = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Βήμα 2: Εγγραφή στο myDATA REST API
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Μετά τη σύνδεση, στην αρχική οθόνη πατήστε το πλακίδιο <strong>«Εγγραφή στο myDATA REST API»</strong>.
      </Typography>

      <Button
        variant="contained"
        size="large"
        fullWidth
        endIcon={<OpenInNewIcon />}
        onClick={() => window.open(MYDATA_PORTAL_URL, '_blank')}
        sx={{ mb: 3, py: 1.5 }}
      >
        Άνοιγμα myDATA Portal
      </Button>

      <CollapsibleGuide title="Δείτε πώς (με εικόνες)" guideKey="subscribe">
        <Typography variant="body2" sx={{ mb: 1 }}>
          Πατήστε το πλακίδιο <strong>«Εγγραφή στο myDATA REST API»</strong> (στο κέντρο):
        </Typography>
        <GuideImage src={guideImages.step2} alt="myDATA Portal - Εγγραφή στο myDATA REST API" />
      </CollapsibleGuide>

      <Alert severity="info" sx={{ mt: 2 }}>
        Η εγγραφή στο API είναι <strong>δωρεάν</strong> και ενεργοποιείται αμέσως.
      </Alert>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Αφού πατήσετε το πλακίδιο, πατήστε «Επόμενο».
      </Typography>
    </Box>
  );

  const renderStepNewUser = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Βήμα 3: Νέα εγγραφή χρήστη
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Στη σελίδα «Στοιχεία μητρώου» πατήστε το κουμπί <strong>«Νέα εγγραφή χρήστη»</strong>.
      </Typography>

      <Button
        variant="contained"
        size="large"
        fullWidth
        endIcon={<OpenInNewIcon />}
        onClick={() => window.open(MYDATA_PORTAL_URL, '_blank')}
        sx={{ mb: 3, py: 1.5 }}
      >
        Άνοιγμα myDATA Portal
      </Button>

      <CollapsibleGuide title="Δείτε πώς (με εικόνες)" guideKey="newUser">
        <Typography variant="body2" sx={{ mb: 1 }}>
          1. Πατήστε το μπλε κουμπί <strong>«Νέα εγγραφή χρήστη»</strong> στο κάτω μέρος της σελίδας:
        </Typography>
        <GuideImage src={guideImages.step3} alt="myDATA Portal - Νέα εγγραφή χρήστη" />

        <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
          2. Στο παράθυρο που ανοίγει, συμπληρώστε το <strong>Όνομα χρήστη</strong> που επιθυμείτε και πατήστε <strong>«Προσθήκη χρήστη»</strong>:
        </Typography>
        <GuideImage src={guideImages.step3b} alt="myDATA Portal - Username popup" />

        <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
          3. Στην ερώτηση «Επιθυμείτε να προσθέσετε τον χρήστη;» πατήστε <strong>«Προσθήκη»</strong>:
        </Typography>
        <GuideImage src={guideImages.step3c} alt="myDATA Portal - Confirm add user" />

        <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
          4. Στο επόμενο παράθυρο πατήστε <strong>«Επιβεβαίωση»</strong>:
        </Typography>
        <GuideImage src={guideImages.step3d} alt="myDATA Portal - Confirm registration success" />
      </CollapsibleGuide>

      <Alert severity="warning" sx={{ mt: 2 }}>
        Αμέσως μετά θα εμφανιστεί το <strong>Subscription Key</strong> — θα το αντιγράψετε στο επόμενο βήμα.
      </Alert>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Αφού πατήσετε «Επιβεβαίωση», πατήστε «Επόμενο».
      </Typography>
    </Box>
  );

  const renderStepCopyKeys = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Βήμα 4: Αντιγραφή Κλειδιών
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Αντιγράψτε αμέσως το Subscription Key από το portal και συμπληρώστε τα πεδία παρακάτω μαζί με το User ID και το ΑΦΜ.
      </Typography>

      <CollapsibleGuide title="Δείτε πώς (με εικόνες)" guideKey="keys">
        <Typography variant="body2" sx={{ mb: 1 }}>
          1. Μετά την επιβεβαίωση εμφανίζεται το <strong>Subscription Key</strong>. Αντιγράψτε το <strong>αμέσως</strong> — δεν θα ξαναεμφανιστεί:
        </Typography>
        <GuideImage src={guideImages.step4} alt="myDATA Portal - Subscription Key popup" />

        <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
          Αποθηκεύστε το apiKey πριν πατήσετε «Κλείσιμο».
        </Alert>

        <Typography variant="body2" sx={{ mb: 1 }}>
          2. Συμπληρώστε στην εφαρμογή:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1 }}>
          <Typography component="li" variant="body2">
            <strong>User ID</strong> = το όνομα χρήστη που δημιουργήσατε στο προηγούμενο βήμα
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Subscription Key</strong> = το κλειδί που μόλις αντιγράψατε
          </Typography>
          <Typography component="li" variant="body2">
            <strong>ΑΦΜ Εταιρείας</strong> = το ΑΦΜ από τα «Στοιχεία μητρώου» στο portal
          </Typography>
        </Box>
        <GuideImage src={guideImages.step4b} alt="Majestify - myDATA credentials fields" />
      </CollapsibleGuide>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          fullWidth
          label="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="Το όνομα χρήστη που δημιουργήσατε στο portal"
          helperText="Το username από το προηγούμενο βήμα (Νέα εγγραφή χρήστη)"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Subscription Key"
          type={showKey ? 'text' : 'password'}
          value={subscriptionKey}
          onChange={(e) => setSubscriptionKey(e.target.value)}
          placeholder="Επικολλήστε το Subscription Key από το portal"
          helperText="Το κλειδί που εμφανίστηκε μετά την επιβεβαίωση — αντιγράψτε το αμέσως"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <VpnKeyIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowKey(!showKey)} edge="end" size="small">
                  {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="ΑΦΜ Εταιρείας"
          value={companyVat}
          onChange={(e) => setCompanyVat(e.target.value)}
          placeholder="123456789"
          helperText={
            initialVat
              ? 'Συμπληρώθηκε αυτόματα από τα στοιχεία της επιχείρησής σας'
              : 'Το ΑΦΜ από τα «Στοιχεία μητρώου» στο portal'
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );

  const renderStepTest = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Βήμα 5: Δοκιμή & Ολοκλήρωση
      </Typography>

      {!saved ? (
        <>
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              bgcolor: 'grey.50',
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Στοιχεία Σύνδεσης:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">User ID:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {userId || '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Subscription Key:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {subscriptionKey ? `${'•'.repeat(8)}${subscriptionKey.slice(-6)}` : '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">ΑΦΜ Εταιρείας:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {companyVat || '—'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {testResult && (
            <Alert
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
              sx={{ mb: 3 }}
            >
              {testResult.success ? testResult.message : testResult.error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={handleTestConnection}
              disabled={testing || !allFieldsFilled}
              startIcon={testing ? <CircularProgress size={20} /> : null}
              sx={{ py: 1.5 }}
            >
              {testing ? 'Δοκιμή σύνδεσης...' : 'Δοκιμή Σύνδεσης'}
            </Button>

            {testResult?.success && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSaveAndFinish}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                sx={{
                  py: 1.5,
                  bgcolor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' },
                }}
              >
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση & Ολοκλήρωση'}
              </Button>
            )}
          </Box>

          {!testResult?.success && allFieldsFilled && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, textAlign: 'center' }}
            >
              Μπορείτε να αποθηκεύσετε χωρίς δοκιμή —{' '}
              <Button
                variant="text"
                size="small"
                onClick={handleSaveAndFinish}
                disabled={saving}
                sx={{ textTransform: 'none' }}
              >
                Αποθήκευση χωρίς δοκιμή
              </Button>
            </Typography>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'success.main' }}>
            Η σύνδεση ολοκληρώθηκε!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Τα στοιχεία αποθηκεύτηκαν κρυπτογραφημένα.
          </Typography>
          <GuideImage src={guideImages.step5} alt="Success" />
        </Box>
      )}
    </Box>
  );

  const stepRenderers = [
    renderStepIntro,
    renderStepRegister,
    renderStepSubscribe,
    renderStepNewUser,
    renderStepCopyKeys,
    renderStepTest,
  ];

  const canGoNext = () => {
    if (activeStep === 4 && !allFieldsFilled) return false;
    return activeStep < STEPS.length - 1;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 3,
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Οδηγός Σύνδεσης myDATA
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          Ακολουθήστε τα βήματα για να συνδέσετε το myDATA API
        </Typography>
      </Box>

      <Box sx={{ px: 3, pt: 3 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ mb: 4 }}
        >
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                sx={{ cursor: index < activeStep ? 'pointer' : 'default' }}
                onClick={() => {
                  if (index < activeStep) setActiveStep(index);
                }}
              >
                {!isMobile && label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ px: 3, pb: 3, minHeight: 300 }}>
        {stepRenderers[activeStep]()}
      </Box>

      {!saved && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'grey.50',
          }}
        >
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBackIcon />}
          >
            Πίσω
          </Button>

          {activeStep < STEPS.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canGoNext()}
              endIcon={<ArrowForwardIcon />}
            >
              Επόμενο
            </Button>
          )}
        </Box>
      )}
    </Paper>
  );
}
